/**
 * The self-contained Ask Docsight overlay: a fixed floating action button
 * (bottom-right) and the docked chat panel it toggles. Mounted once by the app
 * shell — it is not a route.
 *
 * The panel also opens on a global `docsight:open-chat` CustomEvent (dispatched
 * by the Dashboard and Automation screens); an event `detail.message` is
 * auto-submitted on open. Focus returns to the FAB when the panel closes, and
 * the panel remounts on each open so a fresh conversation starts.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import './chatbot.css';

import type { Brand } from '../../api/types';

import { ChatBubbleGlyph, CloseGlyph } from './glyphs';
import { ChatPanel } from './ChatPanel';
import type { AutoSubmit } from './types';

/** The event other screens dispatch to open the panel (optionally with a message). */
export const OPEN_CHAT_EVENT = 'docsight:open-chat';

export interface OpenChatDetail {
  message?: string;
}

export interface AskDocsightProps {
  /** The signed-in caller's own resolved brand — `AppShell`'s single `useMyBrand()` call, threaded
   * down rather than re-fetched here, since this component (unlike `ChatPanel`) stays mounted. */
  brand: Brand;
  /** The brand's logo, or null to show the letter mark instead. */
  logoUrl: string | null;
}

export function AskDocsight({ brand, logoUrl }: AskDocsightProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [autoSubmit, setAutoSubmit] = useState<AutoSubmit | null>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const nonceRef = useRef(0);

  useEffect(() => {
    const handler = (event: Event): void => {
      const detail = (event as CustomEvent<OpenChatDetail | null>).detail;
      const message = detail !== null && typeof detail.message === 'string' ? detail.message : null;
      if (message !== null && message.trim() !== '') {
        nonceRef.current += 1;
        setAutoSubmit({ text: message, nonce: nonceRef.current });
      }
      setOpen(true);
    };
    window.addEventListener(OPEN_CHAT_EVENT, handler);
    return () => {
      window.removeEventListener(OPEN_CHAT_EVENT, handler);
    };
  }, []);

  const close = useCallback((): void => {
    setOpen(false);
    setAutoSubmit(null);
    fabRef.current?.focus();
  }, []);

  const toggle = (): void => {
    if (open) {
      close();
      return;
    }
    setAutoSubmit(null);
    setOpen(true);
  };

  return (
    <div className="ds-chat-anchor">
      {open && <ChatPanel onClose={close} brand={brand} logoUrl={logoUrl} autoSubmit={autoSubmit} />}
      <button
        ref={fabRef}
        type="button"
        className="ds-chat-fab"
        title={brand.chatbot_name}
        aria-label={open ? `Close ${brand.chatbot_name}` : brand.chatbot_name}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={toggle}
      >
        {open ? <CloseGlyph /> : <ChatBubbleGlyph />}
      </button>
    </div>
  );
}
